import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { Button, Card, CardBody, Col, Container, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import Navigator from "src/components/Navigator";
import { toast } from "react-toastify";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, { 
    PaginationProvider, 
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import KeywordManagementService from "src/modules/3rd-party-management/apis/admin/KeywordManagementService";
import { EMPTY_LIST } from "src/common/constants";
import Select from 'react-select';
import { AvField, AvForm } from "availity-reactstrap-validation";

const KeywordManagement = ({
    t
}) => {
    const [filters, setFilters] = useState({
        pageIndex: 1,
        pageSize: 25
    })
    const [ showCreationModal, setShowCreationModal ] = useState(false)
    const [ selectedCategories, setSelectedCategories ] = useState([]) 
    const [ selectedTagToDelete, setSelectedTagToDelete ] = useState(null)

    const toggleCreationModal = () => setShowCreationModal(!showCreationModal)

    const tableColumns = [
        {
            dataField: "index",
            text: t("ID"),
            key: 1,
            sort: true
        },
        {
            dataField: "title",
            text: t("Name"),
            sort: true,
            key: 2
        },
        {
            dataField: "categories",
            text: t("Categories"),
            sort: false,
            key: 3,
            formatter: (cellContent) => {
                return (cellContent || []).map((cat) => cat.name).join(', ')
            }
        },
        {
            dataField: "action",
            text: t("Action"),
            sort: false,
            key: 4,
            formatter: (_cellContent, row) => {
                return (
                    <div className="actions">
                        <Button 
                            size="sm"
                            color="danger" 
                            outline 
                            className="border-0"
                            onClick={() => setSelectedTagToDelete(row.id)}
                        >
                            <i className="ri-delete-bin-line"></i>
                        </Button>
                    </div>
                )
            }
        }
    ];

    const {
        data: tags,
        isFetching: tagsAreFetching,
        isFetched: tagsFetched,
        refetch: refetchTagsList
    } = useQuery({
        queryKey: ["3rd-party-management-get-findings-user-tags-query"],
        queryFn: async () => {
            const service = KeywordManagementService.getInstance();
            const response = await service.fetchTagsList();
            return (response || []).map((item, index) => ({...item, index: index + 1}));
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError() {
            toast(t("Error while fetching keywords"), {
                type: "error"
            });
        }
    });

    const {
        data: categories,
        isFetching: categoriesAreFetching
    } = useQuery({
        queryKey: ["3rd-party-management-get-findings-tags-categories-query"],
        queryFn: async () => {
            const service = KeywordManagementService.getInstance();
            return await service.fetchCategoriesList();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError() {
            toast(t("Error while fetching categories"), {
                type: "error"
            });
        }
    });

    const {
        mutate: createTagMutation,
        isLoading: tagCreationIsLoading
    } = useMutation({
        mutationFn: async (payload) => {
            const service = KeywordManagementService.getInstance();
            return await service.ceateTag(payload);
        },
        onSuccess: () => {
            refetchTagsList()
            setShowCreationModal(false)
            toast(t("Keyword created successfully."), {
                type: "success"
            })
        },
        onError: () => {
            toast(t("Failed to create the keyword. Please try again."), {
                type: "error"
            });
        }
    });

    const {
        mutate: deleteTagMutation,
        isLoading: tagDeletionIsLoading
    } = useMutation({
        mutationFn: async () => {
            const service = KeywordManagementService.getInstance();
            return await service.deleteTag(selectedTagToDelete);
        },
        onSuccess: () => {
            refetchTagsList()
            setSelectedTagToDelete(null)
            toast(t("Keyword deleted successfully."), {
                type: "success"
            })
        },
        onError: () => {
            toast(t("Failed to delete the keyword. Please try again."), {
                type: "error"
            });
        }
    });

    const categoriesOptions = useMemo(() => {
        return (categories || []).map((item) => ({
            value: item.id,
            label: t(item.name)
        }))
    }, [ 
        categories,
        t
    ]);

    useEffect(() => {
        if(showCreationModal){
            setSelectedCategories([]);
        }
    }, [ showCreationModal ]);

    const NoDataIndication = () => (
        (tagsFetched && !tags?.length) ? <div className="alert m-0" role="alert">
            <p style={{
                    textAlign: "center",
                    marginBottom: 0
                }}>
                {t(EMPTY_LIST)}
            </p>
        </div> : <></>
    )

    return (
        <div className='page-content'>
            <Container fluid>
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator 
                            breadCrumbs={[
                                {
                                    title   :   'SIP',
                                    link    :   '/admin/3rd-party/dashboard'
                                },
                                {
                                    title   :   'Keyword Management',
                                    link    :   `/admin/3rd-party/administration/questionnaires`
                                }
                            ]} 
                            pageTitle="Keyword Management"
                        />
                    </Col>
                </Row>

                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardBody>
                                <PaginationProvider
                                    pagination={
                                        paginationFactory({
                                            custom              :   true,
                                            page                :   filters.pageIndex,
                                            sizePerPage         :   filters.pageSize,
                                            totalSize           :   tags?.length || 0,
                                            withFirstAndLast    :   true,
                                            alwaysShowAllBtns   :   true,
                                            prePageText         :   <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                            nextPageText        :   <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                            prePageTitle        :   t('Pre page'),
                                            firstPageTitle      :   t('First page'),
                                            lastPageTitle       :   t('Last page'),
                                            showTotal           :   false,
                                            onPageChange        :   (page) => setFilters((currentState) => ({...currentState, pageIndex: page})),
                                            onSizePerPageChange: (sizePerPage, page) => setFilters((currentState) => ({...currentState, pageIndex: page, pageSize: sizePerPage})),
                                            paginationTotalRenderer :   (from, to, size) => {
                                                return (
                                                    <span className="react-bootstrap-table-pagination-total">
                                                        {`${t('Total Items')} ${size}`}
                                                    </span>
                                                )
                                            },
                                            hideSizePerPage     :   false,
                                            sizePerPageList     :   [
                                                {
                                                    text: '25', 
                                                    value: 25
                                                }, 
                                                {
                                                    text: '50', 
                                                    value: 50
                                                },
                                                {
                                                    text: '100', 
                                                    value: 100
                                                }
                                            ]
                                        })
                                    }>
                                    {
                                        ({
                                            paginationProps,
                                            paginationTableProps
                                        }) => (
                                            <>
                                                <Row>
                                                    <Col sm="12">
                                                        <div className="d-flex gap-4 justify-content-end">
                                                            <Button
                                                                color="primary"
                                                                onClick={() => setShowCreationModal(true)}
                                                            >
                                                                <i className='ri-add-line align-middle me-1' />
                                                                {t("Add Keyword")}
                                                            </Button>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <Row>
                                                    <Col sm="12">
                                                        <BootstrapTable
                                                            loading={ tagsAreFetching }
                                                            overlay={ overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md"/>, text : `${t("Loading")}...` }) }
                                                            defaultSorted={[]}
                                                            keyField={"id"}
                                                            responsive
                                                            bordered={ false }
                                                            data={ tags || [] }
                                                            striped={true}
                                                            columns={tableColumns}
                                                            wrapperClasses="table-responsive"
                                                            classes={"table tpdd-table"}
                                                            headerWrapperClasses={"thead-light"}
                                                            noDataIndication={ () => <NoDataIndication /> }
                                                            { ...paginationTableProps }
                                                        />
                                                    </Col>
                                                </Row>

                                                <Row>
                                                    <Col sm="12">
                                                        <div className="tpdd-pagination-style-1">
                                                            <PaginationTotalStandalone
                                                                { ...paginationProps }
                                                            />
                                                            
                                                            <div>
                                                                <span className={paginationProps.totalSize <= paginationProps.sizePerPage ? 'd-none' : ''}>
                                                                    <PaginationListStandalone
                                                                        { ...paginationProps }
                                                                    />
                                                                </span>
                                                                
                                                                <SizePerPageDropdownStandalone
                                                                    { ...paginationProps }
                                                                />
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </>
                                        )
                                    }
                                </PaginationProvider>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Modal 
                    isOpen={showCreationModal} 
                    toggle={toggleCreationModal} 
                >
                    <AvForm
                        className="needs-validation m-2"
                        onValidSubmit={(_e, values) => {
                            createTagMutation({
                                tag: values.name,
                                categories: selectedCategories.map(item => item.value.toString())
                            })
                        }} 
                    >
                        <ModalHeader 
                            toggle={toggleCreationModal}
                        >
                            {t("Add New Keyword")}
                        </ModalHeader>

                        <ModalBody>
                            <Row>
                                <Col sm='12' className="mb-3">
                                    <Label for="keyword-name">
                                        {t('Keyword Name')}
                                    </Label>
                                    <AvField
                                        id="keyword-name"
                                        name="name"
                                        type="text"
                                        errorMessage={t("This field cannot be blank")}
                                        className="form-control"
                                        validate={{
                                            required: { value: true }
                                        }}
                                    />
                                </Col>

                                <Col sm='12'>
                                    <Label for="keyword-categories">
                                        {t('Categories')}
                                    </Label>
                                    <Select
                                        isMulti
                                        placeholder={t("Select") + "..."}
                                        classNamePrefix="select2-selection"
                                        id='keyword-categories'
                                        options={categoriesOptions}
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        menuPortalTarget={document.body}
                                        onChange={setSelectedCategories}
                                        value={selectedCategories}
                                        isLoading={categoriesAreFetching}
                                        isDisabled={categoriesAreFetching}
                                    />
                                </Col>
                            </Row>
                        </ModalBody>

                        <ModalFooter>
                            <Button
                                color="primary"
                                size="sm"
                                type="submit"
                                disabled={tagCreationIsLoading || categoriesAreFetching}
                            >
                                {tagCreationIsLoading && (
                                    <Spinner 
                                        className="me-1" 
                                        color="white" 
                                        size="sm" 
                                    />
                                )}
                                {t("Add")}
                            </Button>
                        </ModalFooter>
                    </AvForm>
                </Modal>

                <Modal size="md" isOpen={ selectedTagToDelete }>
                    <ModalHeader>
                        {t('Confirm Deletion')}
                    </ModalHeader>
                        
                    <ModalBody>
                        <p>
                            {t('Are you sure you want to delete this keyword?')}
                        </p>
                    </ModalBody>

                    <ModalFooter>
                        <Button 
                            color="danger" 
                            onClick={ () => setSelectedTagToDelete(null) }
                            disabled={tagDeletionIsLoading}
                        > 
                            { t('Cancel') }
                        </Button>

                        <Button 
                            type="button" 
                            color="primary"
                            onClick={deleteTagMutation}
                            disabled={tagDeletionIsLoading}
                        >
                            {
                                tagDeletionIsLoading && (
                                    <Spinner animation="border" variant="primary" size="sm"/>
                                )
                            }
                            { t('Delete') }
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    )
}

export default withNamespaces()(KeywordManagement)